import React, { useState, useEffect } from 'react';
import { Check, CreditCard, Loader2, Crown, Zap, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { paymentAPI } from '../services/api';

const PackageSelection = ({ analysisId, onPackageSelected, onCancel }) => {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const data = await paymentAPI.getPackages();
      setPackages(data.packages);
    } catch (err) {
      setError('Failed to load packages');
      console.error('Failed to fetch packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageType) => {
    setPaymentLoading(true);
    setSelectedPackage(packageType);
    setError('');

    try {
      const originUrl = window.location.origin;
      const sessionData = await paymentAPI.createCheckoutSession(
        analysisId,
        packageType,
        originUrl
      );

      // Redirect to Stripe Checkout
      if (sessionData.checkout_url) {
        window.location.href = sessionData.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
      setPaymentLoading(false);
      setSelectedPackage(null);
    }
  };

  const getPackageIcon = (packageType) => {
    switch (packageType) {
      case 'quick_check':
        return <Zap className="h-8 w-8 text-blue-600" />;
      case 'targeted':
        return <Star className="h-8 w-8 text-purple-600" />;
      case 'monthly':
        return <Crown className="h-8 w-8 text-gold-600" />;
      default:
        return <CreditCard className="h-8 w-8 text-gray-600" />;
    }
  };

  const getPackageColor = (packageType) => {
    switch (packageType) {
      case 'quick_check':
        return 'border-blue-200 bg-blue-50';
      case 'targeted':
        return 'border-purple-200 bg-purple-50';
      case 'monthly':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getRecommendedBadge = (packageType) => {
    if (packageType === 'targeted') {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading packages...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Choose Your Analysis Package
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Select the package that best fits your needs. All packages include AI-powered analysis 
          and actionable recommendations.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-center">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {Object.entries(packages).map(([packageType, packageInfo]) => {
          const isSelected = selectedPackage === packageType;
          const isLoading = paymentLoading && isSelected;

          return (
            <Card 
              key={packageType} 
              className={`relative ${getPackageColor(packageType)} hover:shadow-lg transition-all duration-200 ${
                packageType === 'targeted' ? 'ring-2 ring-purple-200' : ''
              }`}
            >
              {getRecommendedBadge(packageType)}
              
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  {getPackageIcon(packageType)}
                </div>
                <CardTitle className="text-2xl">
                  {packageInfo.name}
                </CardTitle>
                <CardDescription className="text-base">
                  {packageInfo.description}
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">
                    ${packageInfo.amount}
                  </span>
                  {packageType === 'monthly' && (
                    <span className="text-gray-600 ml-2">/month</span>
                  )}
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <ul className="space-y-3 mb-6">
                  {packageInfo.features?.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handlePurchase(packageType)}
                  disabled={paymentLoading}
                  className={`w-full ${
                    packageType === 'targeted' 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : packageType === 'monthly'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Get Started
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-center">
        <Button variant="outline" onClick={onCancel}>
          Back to Dashboard
        </Button>
      </div>

      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-3">What you get with every package:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>AI-powered resume analysis</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>Job description matching</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>ATS compliance check</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>Downloadable PDF report</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageSelection;